import { z } from 'zod';

import { CityKeySchema } from '../city-key/city-key';
import {
  VENUE_CONTACT_EMAIL_MAX_LENGTH,
  VENUE_CONTACT_NAME_MAX_LENGTH,
  VENUE_CONTACT_PHONE_MAX_LENGTH,
  VenueTranslationsSchema,
} from '../venue/venue';

/**
 * Everything about a venue a staff member may write.
 *
 * Exported as a raw **shape** rather than a schema so `VenueUpdateSchema` can
 * `.partial()` it and the two cannot drift: a rule added here is a rule the
 * patch route enforces on the same field, without a second spelling of it.
 *
 * **`contactPhone` is required and `contactEmail` is not.** A partner venue is
 * where two matched members are told to meet in person; when something goes
 * wrong on the day, staff need a number that answers. The column carries the
 * same rule (`not null`) rather than a cross-column "one of phone or email"
 * CHECK, which against a partial patch would surface as a constraint violation
 * instead of a readable refusal.
 */
export const VenueWritableShape = {
  cityKey: CityKeySchema,
  isActive: z.boolean(),
  contactName: z.string().trim().min(1).max(VENUE_CONTACT_NAME_MAX_LENGTH),
  contactPhone: z.string().trim().min(1).max(VENUE_CONTACT_PHONE_MAX_LENGTH),
  /**
   * Nullable **and** required: the admin field sends `null` when it is left
   * empty, never `''` and never an absent key — see the `isEmptyNull` flag on
   * its field definition, which exists for exactly this distinction.
   */
  contactEmail: z.email().max(VENUE_CONTACT_EMAIL_MAX_LENGTH).nullable(),
  translations: VenueTranslationsSchema,
};

export const VenueCreateSchema = z.object({ ...VenueWritableShape }).strict();

export type VenueCreateRequest = z.infer<typeof VenueCreateSchema>;
